import { useContext } from 'react';
import { UseMutationResult, useMutation, useQueryClient } from 'react-query';
import { DEFAULT_ERROR_MESSAGE } from '../../Base.constants';
import { ToastContext } from '../../context/ToastContext';
import { GetAnswerRequest, QuestionAnswer, getAnswer } from '../../services/chat/getAnswer.service';
import { Session } from '../../services/chat/getSessions.service';
import { queryKeys } from '../queryKeyFactory';

export const useGetAnswer = (
  onSettled?: (data: QuestionAnswer | undefined) => void
): UseMutationResult<QuestionAnswer, Error, GetAnswerRequest> => {
  const queryClient = useQueryClient();
  const toast = useContext(ToastContext);

  return useMutation((request: GetAnswerRequest) => getAnswer(request), {
    onSuccess: (data, request) => {
      const sessions: Session[] = queryClient.getQueryData([queryKeys.chat.getSessions]) ?? [];
      const currentSession = sessions?.find((item) => item.session_id === request.sessionId);

      if (currentSession) {
        const newEntry: Session['session_history'][number] = {
          query_seq_id: data.seq_id,
          query: data.query,
          cb_response: data.answer,
          source_documents: data.source_documents,
          query_dtm: new Date().toISOString(),
        };

        const history = [...currentSession.session_history];
        const prevIndex = history.findIndex((item) => item.query_seq_id === newEntry.query_seq_id);

        if (prevIndex >= 0) {
          history.splice(prevIndex, 1, newEntry);
        } else {
          history.push(newEntry);
        }

        queryClient.setQueryData(
          [queryKeys.chat.getSessions],
          [
            ...sessions.filter((item) => item.session_id !== currentSession.session_id),
            {
              ...currentSession,
              session_history: history,
            },
          ]
        );
      } else {
        queryClient.invalidateQueries([queryKeys.chat.getSessions]);
      }
    },
    onError: () => {
      toast.error(DEFAULT_ERROR_MESSAGE);
    },
    onSettled,
  });
};
